import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import {
  Button,
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogHeaderMain,
  DialogTitle,
  Text,
} from '@zrsystem/ui';
import { useEffect, useState, type ReactElement } from 'react';

import {
  useDeleteWhatsAppInboxMessage,
  useEditWhatsAppInboxMessage,
} from '@/features/notification/hooks/use-whatsapp-inbox-message-actions.js';
import { hideWhatsAppInboxMessage } from '@/features/notification/lib/whatsapp-inbox-message-overlay.js';
import { HttpError } from '@/shared/api/types.js';
import { translateError } from '@/shared/i18n/error-messages.js';

export function WhatsAppInboxEditDialog({
  placeId,
  chatId,
  message,
  onClose,
}: {
  placeId: string;
  chatId: string;
  message: WhatsAppInboxMessage;
  onClose: () => void;
}): ReactElement {
  const editMessage = useEditWhatsAppInboxMessage(placeId, chatId);
  const [draft, setDraft] = useState(message.body ?? '');
  const [error, setError] = useState<string | null>(null);
  const canSave = draft.trim().length > 0 && !editMessage.isPending;

  useEffect(() => {
    setDraft(message.body ?? '');
    setError(null);
  }, [message]);

  async function save() {
    const text = draft.trim();
    if (text.length === 0) {
      return;
    }
    setError(null);
    try {
      await editMessage.mutateAsync({ messageId: message.id, text });
      onClose();
    } catch (caught) {
      setError(
        caught instanceof HttpError
          ? translateError(caught.code, caught.message)
          : translateError('HTTP_ERROR'),
      );
    }
  }

  return (
    <DialogBackdrop
      onClick={() => {
        if (!editMessage.isPending) onClose();
      }}
    >
      <Dialog
        className="whatsapp-inbox-dialog"
        aria-labelledby="whatsapp-inbox-edit-title"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogHeaderMain>
            <DialogTitle id="whatsapp-inbox-edit-title">Editar mensagem</DialogTitle>
          </DialogHeaderMain>
        </DialogHeader>
        <DialogBody>
          <textarea
            className="whatsapp-inbox-dialog__text"
            value={draft}
            maxLength={4000}
            rows={4}
            aria-label="Texto da mensagem"
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          {error !== null ? (
            <Text as="p" role="alert">
              {error}
            </Text>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" disabled={editMessage.isPending} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canSave}
            onClick={() => {
              void save();
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogBackdrop>
  );
}

export function WhatsAppInboxDeleteDialog({
  placeId,
  chatId,
  message,
  onClose,
}: {
  placeId: string;
  chatId: string;
  message: WhatsAppInboxMessage;
  onClose: () => void;
}): ReactElement {
  const deleteMessage = useDeleteWhatsAppInboxMessage(placeId, chatId);
  const [error, setError] = useState<string | null>(null);

  function deleteForMe() {
    hideWhatsAppInboxMessage(placeId, message.id);
    onClose();
  }

  async function deleteForEveryone() {
    setError(null);
    try {
      await deleteMessage.mutateAsync(message.id);
      onClose();
    } catch (caught) {
      setError(
        caught instanceof HttpError
          ? translateError(caught.code, caught.message)
          : translateError('HTTP_ERROR'),
      );
    }
  }

  return (
    <DialogBackdrop
      onClick={() => {
        if (!deleteMessage.isPending) onClose();
      }}
    >
      <Dialog
        className="whatsapp-inbox-dialog"
        aria-labelledby="whatsapp-inbox-delete-title"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogHeaderMain>
            <DialogTitle id="whatsapp-inbox-delete-title">Deseja apagar a mensagem?</DialogTitle>
          </DialogHeaderMain>
        </DialogHeader>
        <DialogBody>
          <Text as="p">
            Apagar para mim tira a mensagem desta conversa. Apagar para todos também some para o
            cliente no WhatsApp.
          </Text>
          {error !== null ? (
            <Text as="p" role="alert">
              {error}
            </Text>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={deleteMessage.isPending}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={deleteMessage.isPending}
            onClick={deleteForMe}
          >
            Apagar para mim
          </Button>
          {message.fromMe ? (
            <Button
              type="button"
              variant="danger"
              disabled={deleteMessage.isPending}
              onClick={() => {
                void deleteForEveryone();
              }}
            >
              Apagar para todos
            </Button>
          ) : null}
        </DialogFooter>
      </Dialog>
    </DialogBackdrop>
  );
}
